// Cave84 — Bottle Detail (Phase 1 Week 5)
// Spec §6.3 Screen 4. Shows full provenance, purchase details, quick actions.

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useBottle, useConsumptionForBottle } from '@/hooks/useBottles';
import { useVintage } from '@/hooks/useVintages';
import { useWine } from '@/hooks/useWines';
import { useProducer } from '@/hooks/useProducers';
import { useCellar } from '@/hooks/useCellars';
import { useCellarLocations } from '@/hooks/useLocations';
import OpenBottleModal from '@/components/OpenBottleModal';
import TransferBottleModal from '@/components/TransferBottleModal';
import ChangeStatusModal from '@/components/ChangeStatusModal';
import { SOURCE_LABELS } from '@/services/bottleLabels';
import EditBottleModal from '@/components/EditBottleModal';
import TastingNoteModal from '@/components/TastingNoteModal';
import { Money } from '@/components/Money';
import { useIsOwner } from '@/stores/auth.store';
import type { Bottle } from '@cave84/shared-types';

// ---------------------------------------------------------------------------
// Label helpers
// ---------------------------------------------------------------------------

const FORMAT_LABELS: Record<string, string> = {
  half: 'Half (375ml)', '750ml': 'Standard (750ml)', magnum: 'Magnum (1.5L)',
  double_magnum: 'Double Magnum (3L)', jeroboam: 'Jeroboam (4.5L)',
  imperial: 'Impériale (6L)', salmanazar: 'Salmanazar (9L)',
  methuselah: 'Methuselah (6L Champ.)', other: 'Other',
};

const CONDITION_LABELS: Record<string, string> = {
  perfect: 'Perfect', minor: 'Minor', scuffed: 'Scuffed',
  torn: 'Torn', stained: 'Stained', missing: 'Missing',
};

const FILL_LABELS: Record<string, string> = {
  into_neck: 'Into Neck', base_of_neck: 'Base of Neck',
  top_shoulder: 'Top Shoulder', upper_shoulder: 'Upper Shoulder',
  mid_shoulder: 'Mid Shoulder', low_shoulder: 'Low Shoulder',
};

const STATUS_LABELS: Record<string, string> = {
  in_cellar: 'In Cellar', consumed: 'Consumed', gifted: 'Gifted',
  sold: 'Sold', lost: 'Lost', broken: 'Broken', trading: 'Trading',
  virtual_pending: 'Virtual / Pending',
};

// ---------------------------------------------------------------------------
// Modal state
// ---------------------------------------------------------------------------

type ModalState =
  | { kind: 'none' }
  | { kind: 'open' }
  | { kind: 'transfer' }
  | { kind: 'changeStatus' }
  | { kind: 'edit' }
  | { kind: 'tastingNote' };

// ---------------------------------------------------------------------------
// Info row helper
// ---------------------------------------------------------------------------

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex justify-between items-baseline py-2.5 border-b border-border-subtle last:border-0">
      <span className="text-sm text-text-muted uppercase tracking-wide">{label}</span>
      <span className="text-base text-text-primary font-medium">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail view (when bottle is loaded)
// ---------------------------------------------------------------------------

function BottleDetail({ bottle }: { bottle: Bottle }) {
  const navigate = useNavigate();
  const isOwner = useIsOwner();
  const [modal, setModal] = useState<ModalState>({ kind: 'none' });

  const { data: vintage } = useVintage(bottle.vintage_id);
  const { data: wine } = useWine(vintage?.wine_id);
  const { data: producer } = useProducer(wine?.producer_id);
  const { data: cellar } = useCellar(bottle.current_cellar_id);
  const { data: locations = [] } = useCellarLocations(bottle.current_cellar_id);
  const currentLocation = locations.find(l => l.id === bottle.current_location_id);

  const isActive = bottle.status === 'in_cellar';

  // Drinking location + tasting note captured by OpenBottleModal — only ever
  // exists once the bottle has actually been consumed.
  const { data: consumption } = useConsumptionForBottle(bottle.id, !isActive);
  const drinkingLocation = consumption?.event?.location;
  const tastingNote = consumption?.tastingNote;

  // Format purchase date
  const purchaseDate = bottle.purchase_date
    ? (typeof bottle.purchase_date === 'object' && 'toDate' in bottle.purchase_date
        ? (bottle.purchase_date as { toDate: () => Date }).toDate().toLocaleDateString('en-GB')
        : new Date(bottle.purchase_date as unknown as string).toLocaleDateString('en-GB'))
    : null;

  return (
    <div className="min-h-screen bg-surface-0">
      {/* Minimal sticky top bar — back button only */}
      <div className="sticky top-0 z-10 bg-surface-1 border-b border-border-medium px-4 py-3 flex items-center">
        <button type="button" onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors text-sm"
          aria-label="Back">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Inventory
        </button>
      </div>

      <div className="max-w-xl mx-auto px-4 py-6 space-y-6">
        {/* Wine hero card */}
        <div className="bg-surface-1 rounded-lg border border-border-medium p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-text-muted uppercase tracking-wide mb-1.5">{producer?.name}</p>
              <h1 className="font-display text-3xl font-medium text-text-primary leading-tight tracking-tight">
                {vintage?.year} {wine?.cuvee_name ?? '—'}
              </h1>
            </div>
            <span className={`flex-shrink-0 px-3 py-1.5 rounded text-sm font-semibold border tracking-wide ${
              isActive
                ? 'bg-gold/10 border-gold/30 text-gold'
                : 'bg-surface-3 border-border-medium text-text-muted'
            }`}>
              {STATUS_LABELS[bottle.status]}
            </span>
          </div>
          {isOwner && (
            <div className="mt-5 pt-4 border-t border-border-subtle flex items-center justify-end">
              <button
                type="button"
                onClick={() => setModal({ kind: 'edit' })}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-surface-2 border border-border-medium text-text-secondary text-base hover:border-gold/50 hover:text-gold transition-colors"
                aria-label="Edit bottle"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit bottle
              </button>
            </div>
          )}
        </div>
        {/* Special occasion banner */}
        {bottle.is_special_occasion && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-gold/10 border border-gold/30">
            <span className="text-xl">🌟</span>
            <div>
              <p className="text-base font-medium text-gold">Special Occasion</p>
              {bottle.special_occasion_reason && (
                <p className="text-sm text-gold/70">{bottle.special_occasion_reason}</p>
              )}
            </div>
          </div>
        )}

        {/* Location card */}
        {cellar && (
          <div className="bg-surface-1 rounded-lg border border-border-medium p-5">
            <h2 className="text-sm font-medium text-text-muted uppercase tracking-wide mb-3">Location</h2>
            <div className="flex items-center gap-3">
              <span className="text-3xl">{cellar.icon ?? '🍾'}</span>
              <div>
                <p className="text-base font-semibold text-text-primary">{cellar.name}</p>
                {currentLocation && (
                  <p className="text-sm text-text-muted">{currentLocation.name}</p>
                )}
                {cellar.country && (
                  <p className="text-sm text-text-muted">{cellar.country}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Provenance */}
        <div className="bg-surface-1 rounded-lg border border-border-medium p-5">
          <h2 className="text-sm font-medium text-text-muted uppercase tracking-wide mb-3">Provenance</h2>
          <InfoRow label="Format" value={FORMAT_LABELS[bottle.format]} />
          <InfoRow label="Fill Level" value={FILL_LABELS[bottle.fill_level]} />
          <InfoRow label="Label" value={CONDITION_LABELS[bottle.label_condition]} />
          <InfoRow label="Capsule" value={CONDITION_LABELS[bottle.capsule_condition]} />
          {bottle.in_OWC && <InfoRow label="OWC" value="Yes — Original Wooden Case" />}
        </div>

        {/* Purchase — Owner-only: Guests get no purchase-price visibility */}
        {isOwner && (
          <div className="bg-surface-1 rounded-lg border border-border-medium p-5">
            <h2 className="text-sm font-medium text-text-muted uppercase tracking-wide mb-3">Purchase</h2>
            <InfoRow label="Source" value={SOURCE_LABELS[bottle.purchase_source] ?? bottle.purchase_source} />
            {bottle.purchase_source_detail && (
              <InfoRow label="Detail" value={bottle.purchase_source_detail} />
            )}
            <InfoRow label="Date" value={purchaseDate} />
            <InfoRow
              label="Cost"
              value={<Money value={bottle.cost_landed} currency={bottle.cost_currency} minWidthCh={7} />}
            />
            {bottle.cost_currency !== 'EUR' && (
              <InfoRow label="EUR at Purchase" value={<Money value={bottle.cost_eur_at_purchase} currency="EUR" minWidthCh={7} />} />
            )}
            {bottle.purchase_lot_id && (
              <InfoRow label="Lot" value={bottle.purchase_lot_id} />
            )}
          </div>
        )}

        {/* Tasting note — where it was drunk + drinking notes from Open Bottle.
            Always shown once consumed (even with nothing recorded yet) so the
            owner can add it retroactively — bulk-imported bottles land here
            already consumed and never went through OpenBottleModal. */}
        {!isActive && (
          <div className="bg-surface-1 rounded-lg border border-border-medium p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-medium text-text-muted uppercase tracking-wide">Tasting Note</h2>
              {isOwner && (
                <button
                  type="button"
                  onClick={() => setModal({ kind: 'tastingNote' })}
                  className="text-sm text-text-muted hover:text-gold transition-colors"
                >
                  {drinkingLocation || tastingNote ? 'Edit' : '+ Add'}
                </button>
              )}
            </div>
            {drinkingLocation || tastingNote?.free_text || tastingNote?.personal_score_100pt != null ? (
              <>
                <InfoRow label="Drunk At" value={drinkingLocation} />
                {tastingNote?.personal_score_100pt != null && (
                  <InfoRow label="Score" value={`${tastingNote.personal_score_100pt} pts`} />
                )}
                {tastingNote?.free_text && (
                  <p className="text-base text-text-secondary whitespace-pre-wrap mt-3 first:mt-0">
                    {tastingNote.free_text}
                  </p>
                )}
              </>
            ) : (
              <p className="text-base text-text-muted">Nothing recorded yet.</p>
            )}
          </div>
        )}

        {/* Notes */}
        {bottle.notes && (
          <div className="bg-surface-1 rounded-lg border border-border-medium p-5">
            <h2 className="text-sm font-medium text-text-muted uppercase tracking-wide mb-2">Notes</h2>
            <p className="text-base text-text-secondary whitespace-pre-wrap">{bottle.notes}</p>
          </div>
        )}

        {/* Wine analysis link */}
        {wine && (
          <Link
            to={vintage ? `/wines/${wine.id}?vintage=${vintage.year}` : `/wines/${wine.id}`}
            className="flex items-center justify-between px-5 py-4 rounded-lg bg-surface-1 border border-border-medium hover:bg-surface-2 transition-colors group"
          >
            <div>
              <p className="text-base font-medium text-text-primary">Wine Analysis</p>
              <p className="text-sm text-text-muted">{wine.cuvee_name}</p>
            </div>
            <svg className="w-5 h-5 text-text-muted group-hover:text-text-secondary transition-colors"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        )}

        {/* Actions — only if in_cellar, and Owner-only (Guests cannot mutate). */}
        {isActive && isOwner && (
          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              type="button"
              onClick={() => setModal({ kind: 'open' })}
              className="flex items-center justify-center gap-2 px-4 py-3.5 rounded-lg bg-claret text-bg font-semibold text-base hover:bg-claret/90 transition-colors"
            >
              🍷 Open Bottle
            </button>
            <button
              type="button"
              onClick={() => setModal({ kind: 'transfer' })}
              className="flex items-center justify-center gap-2 px-4 py-3.5 rounded-lg bg-surface-2 border border-border-medium text-text-primary text-base hover:bg-surface-3 transition-colors"
            >
              ↗ Transfer
            </button>
            <button
              type="button"
              onClick={() => setModal({ kind: 'changeStatus' })}
              className="col-span-2 flex items-center justify-center gap-2 px-4 py-3.5 rounded-lg bg-surface-2 border border-border-medium text-text-secondary text-base hover:bg-surface-3 transition-colors"
            >
              ⋯ Change Status
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      {modal.kind === 'open' && (
        <OpenBottleModal bottle={bottle} onClose={() => setModal({ kind: 'none' })} />
      )}
      {modal.kind === 'transfer' && (
        <TransferBottleModal bottle={bottle} onClose={() => setModal({ kind: 'none' })} />
      )}
      {modal.kind === 'changeStatus' && (
        <ChangeStatusModal bottle={bottle} onClose={() => setModal({ kind: 'none' })} />
      )}
      {modal.kind === 'edit' && (
        <EditBottleModal
          bottle={bottle}
          producer={producer}
          onClose={() => setModal({ kind: 'none' })}
        />
      )}
      {modal.kind === 'tastingNote' && (
        <TastingNoteModal
          bottle={bottle}
          wineId={wine?.id}
          existing={consumption ?? { event: null, tastingNote: null }}
          onClose={() => setModal({ kind: 'none' })}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page wrapper (handles loading + not-found)
// ---------------------------------------------------------------------------

export default function BottleDetailPage() {
  const { bottleId } = useParams<{ bottleId: string }>();
  const { data: bottle, isLoading } = useBottle(bottleId);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-0 flex items-center justify-center">
        <p className="text-text-muted text-sm animate-pulse">Loading bottle…</p>
      </div>
    );
  }

  if (!bottle) {
    return (
      <div className="min-h-screen bg-surface-0 flex flex-col items-center justify-center gap-3">
        <p className="text-text-muted text-sm">Bottle not found</p>
        <Link to="/inventory" className="text-gold text-sm hover:underline">← Back to inventory</Link>
      </div>
    );
  }

  return <BottleDetail bottle={bottle} />;
}
